import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { useBackNavigationInfo } from 'src/hooks';

import { ChallengesService, ErrorReportingService } from 'src/services';
import { ChallengeId, ChallengeSessionId, ChallengeStepId } from 'src/models/Challenge';
import { useUserState } from 'src/contexts/UserContext';

export const usePracticePlan = (id?: ChallengeId, challengeSessionId?: ChallengeSessionId) => {
  const { isRegularUser } = useUserState();
  return useQuery(
    ['practicePlans', id ?? challengeSessionId],
    () => ChallengesService.getByIdV2((id ?? challengeSessionId)!, {
      excludeUnpublishedSteps: isRegularUser,
      bySession: challengeSessionId != null,
    }),
    { refetchOnWindowFocus: false },
  );
};

export const usePracticePlanSession = (id: ChallengeSessionId) => useQuery(
  ['practicePlanSessions', id],
  () => ChallengesService.getSessionById(id),
  { refetchOnWindowFocus: false },
);

export const useToggleStepCompleted = () => {
  const queryClient = useQueryClient();
  return useCallback((
    practicePlanId: ChallengeId,
    stepId: ChallengeStepId,
    status: boolean,
  ) => {
    ChallengesService.toggleChallengeStepCompleted(stepId, status)
      .then(() => queryClient.invalidateQueries(['practicePlans', practicePlanId]))
      .catch(ErrorReportingService.report);
  }, [queryClient]);
};

export const useReturnLink = () => {
  const backInfo = useBackNavigationInfo();

  if (backInfo.link) {
    return {
      to: backInfo.link,
      label: backInfo.linkLabel ?? 'Plany praktyki',
    };
  }

  return {
    to: '/practice-plans',
    label: 'Plany praktyki',
  };
};

export const useMarkVisited = (id: ChallengeId, stepId?: ChallengeStepId) => useEffect(
  () => {
    if (id == null) {
      return;
    }
    ChallengesService
      .markChallengeVisited(id)
      .catch(e => ErrorReportingService.report(e));
  },
  [id, stepId],
);
